import React, { useState, useEffect, useMemo } from "react";
import {
    Box,
    Typography,
    Container,
    Paper,
    Chip,
    IconButton,
    Menu,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Grid,
    TextField,
    FormControl,
    InputLabel,
    Select,
    Divider,
} from "@mui/material";
import {
    MoreVert,
    Edit,
    Delete,
    Visibility,
} from "@mui/icons-material";
import dayjs from "dayjs";

import CustomTable from "../../components/common/CustomTable";
import SkeletonLoader from "../../components/common/SkeletonLoader";
import CustomHeader from "../../components/common/CustomHeader";
import SearchBox from "../../components/common/CustomSearchBar";
import { landingApis } from "../../api/landingApi";
import CustomSnackbar from "../../components/common/CustomSnackbar";
import { useTheme } from "@mui/material/styles";

const STATUS_OPTIONS = ["NEW", "CONTACTED", "SCHEDULED", "COMPLETED", "CLOSED"];

const DemoUserListPage = () => {
    const theme = useTheme();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");

    // Menu state for actions
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedUser, setSelectedUser] = useState(null);

    // Modals state
    const [viewOpen, setViewOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);

    // Form state for edit
    const [editForm, setEditForm] = useState({});
    const [updating, setUpdating] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "info",
    });

    const showSnackbar = (message, severity = "info") => {
        setSnackbar({ open: true, message, severity });
    };

    /* ---------------- FETCH DATA ---------------- */
    const fetchDemoUsers = async () => {
        try {
            setLoading(true);
            const response = await landingApis.getAllDemoUser();

            if (response && response.status === 200 && Array.isArray(response.data)) {
                setUsers(response.data);
            } else {
                showSnackbar(response?.message || "Failed to fetch demo requests.", "error");
                setUsers([]);
            }
        } catch (error) {
            console.error("Error fetching demo users:", error);
            showSnackbar("Something went wrong while loading demo users.", "error");
            setUsers([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDemoUsers();
    }, []);

    /* ---------------- SEARCH ---------------- */
    const filteredUsers = useMemo(() => {
        const q = search.toLowerCase();
        return users.filter((u) =>
            [
                u.name,
                u.email,
                u.company,
                u.status,
            ].some((field) => field?.toLowerCase().includes(q))
        );
    }, [users, search]);

    const hasUsers = users.length > 0;

    /* ---------------- ACTIONS ---------------- */
    const handleMenuClick = (event, user) => {
        setAnchorEl(event.currentTarget);
        setSelectedUser({
            ...user,
            phone: user.phone || "",
            designation: user.designation || "",
        });
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleOpenView = () => {
        handleMenuClose();
        setViewOpen(true);
    };

    const handleOpenEdit = () => {
        handleMenuClose();
        setEditForm({ ...selectedUser });
        setEditOpen(true);
    };

    const handleOpenDelete = () => {
        handleMenuClose();
        setDeleteOpen(true);
    };

    const handleEditChange = (e) => {
        const { name, value } = e.target;
        setEditForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleUpdate = async () => {
        try {
            setUpdating(true);
            // Exclude updated_at and un-updatable fields if necessary
            // const payload = { ...editForm };

            const response = await landingApis.updateDemoUser(selectedUser.id, { "status": editForm["status"] });

            if (response && (response.status === 200 || response.status === 201)) {
                showSnackbar("User updated successfully", "success");
                setEditOpen(false);
                fetchDemoUsers();
            } else {
                showSnackbar(response?.message || "Failed to update user", "error");
            }
        } catch (error) {
            console.error("Error updating user:", error);
            showSnackbar("Something went wrong while updating", "error");
        } finally {
            setUpdating(false);
        }
    };

    const handleDelete = async () => {
        try {
            setDeleting(true);
            const response = await landingApis.deleteDemoUser(selectedUser.id);

            if (response && response.status === 200) {
                showSnackbar("User deleted successfully", "success");
                setDeleteOpen(false);
                fetchDemoUsers();
            } else {
                showSnackbar(response?.message || "Failed to delete user", "error");
            }
        } catch (error) {
            console.error("Error deleting user:", error);
            showSnackbar("Something went wrong while deleting", "error");
        } finally {
            setDeleting(false);
        }
    };


    /* ---------------- TABLE COLUMNS ---------------- */
    const columns = [
        {
            label: "Name",
            field: "name",
        },
        {
            label: "Email",
            field: "email",
        },
        {
            label: "Company",
            field: "company",
            render: (val) => val || "—"
        },
        {
            label: "Country Code",
            field: "country_code",
            render: (val) => val || "N/A"
        },
        {
            label: "Phone",
            field: "phone",
            render: (val) => val || "N/A"
        },
        {
            label: "Designation",
            field: "designation",
            render: (val) => val || "N/A"
        },
        {
            label: "Created At",
            field: "created_at",
            render: (val) => dayjs(val).format("DD MMM YYYY, hh:mm A"),
        },
        {
            label: "Updated At",
            field: "updated_at",
            render: (val) => val && val !== "N/A" ? dayjs(val).format("DD MMM YYYY, hh:mm A") : "Not updated Yet"
        },
        {
            label: "Status",
            field: "status",
            render: (val) => {
                const statusMap = {
                    NEW: "info",
                    CONTACTED: "warning",
                    SCHEDULED: "secondary",
                    COMPLETED: "success",
                    CLOSED: "default",
                };
                const displayVal = val ? val.toUpperCase() : "NEW";
                return (
                    <Chip
                        size="small"
                        variant="outlined"
                        label={displayVal}
                        color={statusMap[displayVal] || "default"}
                    />
                );
            },
        },
        {
            label: "Action",
            field: "action",
            render: (_, row) => (
                <>
                    <IconButton onClick={(e) => handleMenuClick(e, row)} size="small">
                        <MoreVert />
                    </IconButton>
                </>
            ),
        },
    ];

    const fieldOrder = [
        "name",
        "email",
        "country_code",
        "phone",
        "company",
        "designation",
        "status",
        "created_at",
        "updated_at",
    ];

    /* ---------------- RENDER ---------------- */
    return (
        <Box sx={{ minHeight: "100vh" }}>
            <CustomHeader title="Demo Requests" />

            <Container maxWidth="xl" sx={{ mt: 3, mb: 4 }}>
                {loading ? (
                    <SkeletonLoader type="table" rows={6} columns={columns} />
                ) : !hasUsers && !search ? (
                    <Paper
                        elevation={3}
                        sx={{
                            mt: 4,
                            p: 6,
                            textAlign: "center",
                            borderRadius: 3,
                        }}
                    >
                        <Typography variant="h6" fontWeight="bold">
                            No Demo Requests Found
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Requests submitted from the landing page will appear here.
                        </Typography>
                    </Paper>
                ) : (
                    <Box sx={{ width: "100%" }}>
                        {/* Search */}
                        <Box sx={{ mb: 2, width: "100%" }}>
                            <SearchBox
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                onClear={() => setSearch("")}
                                placeholder="Search by name, email, company, or status..."
                                minWidth={"100%"}
                                height={38}
                                fontSize="0.85rem"
                            />
                        </Box>

                        <Typography sx={{ mb: 1, color: "gray" }}>
                            Showing {filteredUsers.length} entries
                        </Typography>

                        <Box
                            sx={{
                                borderRadius: 3,
                                overflow: "hidden",
                                bgcolor: "#111",
                                boxShadow: "0 4px 20px rgba(0,0,0,0.5)",
                            }}
                        >
                            <CustomTable
                                columns={columns}
                                rows={filteredUsers}
                                emptyMessage="No Requests Found!"
                                sx={{
                                    "& .MuiTableCell-root": {
                                        color: "white",
                                        borderBottom: "1px solid rgba(255,255,255,0.1)",
                                    },
                                    "& .MuiTableHead-root": {
                                        backgroundColor: "#222",
                                    },
                                    "& .MuiTableHead-root .MuiTableCell-root": {
                                        color: "lightgray",
                                        fontWeight: "bold",
                                    },
                                    "& .MuiTableRow-root:hover": {
                                        backgroundColor: "rgba(255,255,255,0.05)",
                                    },
                                }}
                            />
                        </Box>
                    </Box>
                )}
            </Container>

            {/* Action Menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                PaperProps={{
                    sx: { width: 150, boxShadow: "0 4px 12px rgba(0,0,0,0.1)" },
                }}
            >
                <MenuItem onClick={handleOpenView}>
                    <Visibility fontSize="small" sx={{ mr: 1, color: "gray" }} /> View
                </MenuItem>
                <MenuItem onClick={handleOpenEdit}>
                    <Edit fontSize="small" sx={{ mr: 1, color: "primary.main" }} /> Edit
                </MenuItem>
                <MenuItem onClick={handleOpenDelete} sx={{ color: "error.main" }}>
                    <Delete fontSize="small" sx={{ mr: 1 }} /> Delete
                </MenuItem>
            </Menu>

            {/* View Modal */}
            <Dialog open={viewOpen} onClose={() => setViewOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle
                    variant="h5"
                    align="center"
                    sx={{
                        fontWeight: 700,
                        color: theme.palette.primary.main,
                        pb: 0.3,
                    }}>
                    Request Details
                </DialogTitle>
                <Typography
                    variant="body2"
                    sx={{
                        color: "text.secondary",
                        textAlign: "center",
                        mb: 1,
                        fontSize: "0.85rem",
                    }}>
                    User detail of who is requesting for demo.
                </Typography>
                <Divider />

                <DialogContent dividers>
                    {selectedUser && (
                        <Grid container spacing={2} sx={{ mt: 0.5 }}>
                            {Object.entries(selectedUser).map(([key, value]) => {
                                if (key === "id") return null;
                                if (typeof value === "object" && value !== null) return null;

                                let displayValue = value || "";
                                if (key === "created_at" || key === "updated_at") {
                                    if (value && value !== "N/A") {
                                        displayValue = dayjs(value).format("YYYY-MM-DD HH:mm");
                                    } else if (key === "updated_at") {
                                        displayValue = "Not updated Yet";
                                    } else {
                                        displayValue = "N/A";
                                    }
                                } else if (!value) {
                                    displayValue = "N/A";
                                }

                                return (
                                    <Grid item xs={12} sm={6} key={key}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label={key.replace(/_/g, " ").toUpperCase()}
                                            value={displayValue}
                                            disabled
                                            InputProps={{
                                                readOnly: true,
                                            }}
                                        />
                                    </Grid>
                                );
                            })}
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setViewOpen(false)} variant="contained" size="small">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit Modal */}
            <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle variant="h5"
                    align="center"
                    sx={{
                        fontWeight: 700,
                        color: theme.palette.primary.main,
                        pb: 0.3,
                    }}>
                    Edit Request
                </DialogTitle>
                <Typography
                    variant="body2"
                    sx={{
                        color: "text.secondary",
                        textAlign: "center",
                        mb: 1,
                        fontSize: "0.85rem",
                    }}>
                    Edit details of the demo request.
                </Typography>
                <Divider />
                <DialogContent dividers>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        {fieldOrder.map((key) => {
                            const value = editForm[key];

                            if (key === "status") {
                                return (
                                    <Grid item xs={12} sm={6} key={key}>
                                        <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            label="STATUS"
                                            name="status"
                                            value={editForm.status || "NEW"}
                                            onChange={handleEditChange}
                                        >
                                            {STATUS_OPTIONS.map((opt) => (
                                                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>
                                );
                            }

                            let displayValue = value || "";
                            if (key === "created_at" || key === "updated_at") {
                                if (value && value !== "N/A") {
                                    displayValue = dayjs(value).format("YYYY-MM-DD HH:mm");
                                } else if (key === "updated_at") {
                                    displayValue = "Not updated Yet";
                                } else {
                                    displayValue = "N/A";
                                }
                            } else if (!value) {
                                displayValue = "N/A";
                            }

                            return (
                                <Grid item xs={12} sm={6} key={key}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label={key.replace(/_/g, " ").toUpperCase()}
                                        name={key}
                                        value={displayValue}
                                        disabled
                                        InputProps={{
                                            readOnly: true,
                                        }}
                                    />
                                </Grid>
                            );
                        })}
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setEditOpen(false)} color="inherit" disabled={updating}>
                        Cancel
                    </Button>
                    <Button onClick={handleUpdate} variant="contained" color="primary" disabled={updating}>
                        {updating ? "Updating..." : "Update"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation */}
            <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete the demo request for <b>{selectedUser?.name}</b>?
                        This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setDeleteOpen(false)} color="inherit" disabled={deleting}>
                        Cancel
                    </Button>
                    <Button onClick={handleDelete} color="error" variant="contained" disabled={deleting}>
                        {deleting ? "Deleting..." : "Delete"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <CustomSnackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
            />
        </Box>
    );
};

export default DemoUserListPage;
